import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { useHaptic } from '../../hooks/useHaptic';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string; // e.g. 'max-w-xl', 'max-w-2xl'
}

export function Sheet({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-xl',
}: SheetProps) {
  const { lightTap } = useHaptic();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Focus lock and Escape key listener
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Lock body scroll
    document.body.classList.add('overflow-hidden-lock');
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('overflow-hidden-lock');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleCloseClick = () => {
    lightTap();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Blur Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/30 backdrop-blur-xs z-40"
          />

          {/* Drawer Panel */}
          <motion.div
            ref={drawerRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 220 }}
            className={`fixed right-0 top-0 h-screen w-full ${maxWidth} bg-white border-l border-slate-200 shadow-2xl z-50 overflow-y-auto flex flex-col`}
          >
            {/* Drawer Header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-sans">
                {title}
              </span>
              <button
                onClick={handleCloseClick}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-100"
                aria-label="Close panel"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6 font-sans">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
